import type { CdmoSafeIntroductionRequest } from './supabase';
import { normalizeRegulatoryCertificationLabel } from './gatekeeperFilters';

export type WarmIntroShareFieldKey =
  | 'productTypes'
  | 'specificServices'
  | 'requiredCertifications'
  | 'partnerType'
  | 'developmentStage'
  | 'supportNeeds'
  | 'batchSizeRange'
  | 'timeline'
  | 'budgetRange'
  | 'preferredRegions'
  | 'companyName'
  | 'accountCompanyStage';

export interface SharedFieldSettings {
  productTypes: boolean;
  specificServices: boolean;
  requiredCertifications: boolean;
  partnerType: boolean;
  developmentStage: boolean;
  supportNeeds: boolean;
  batchSizeRange: boolean;
  timeline: boolean;
  budgetRange: boolean;
  preferredRegions: boolean;
  companyName: boolean;
  accountCompanyStage: boolean;
}

export interface SharedProjectBrief {
  productTypes?: string[];
  specificServices?: string[];
  requiredCertifications?: string[];
  partnerType?: string;
  developmentStage?: string;
  supportNeeds?: string[];
  batchSizeRange?: string;
  timeline?: string;
  budgetRange?: string;
  preferredRegions?: string[];
  companyName?: string;
  accountCompanyStage?: string;
}

export const DEFAULT_SHARED_FIELD_SETTINGS: SharedFieldSettings = {
  productTypes: true,
  specificServices: true,
  requiredCertifications: true,
  partnerType: true,
  developmentStage: true,
  supportNeeds: true,
  batchSizeRange: true,
  timeline: true,
  budgetRange: false,
  preferredRegions: true,
  companyName: false,
  accountCompanyStage: false,
};

export const MINIMUM_RECOMMENDED_KEYS: WarmIntroShareFieldKey[] = [
  'productTypes',
  'specificServices',
  'developmentStage',
  'supportNeeds',
  'timeline',
  'preferredRegions',
];

interface MatchingFilters {
  productType: string[];
  detailedServices: string[];
  certifications: string[];
  cdmoPartnerType: string;
  developmentStage: string;
  supportType: string[];
  batchSize: string;
  speedToStart: string;
  budget: string;
  location: string[];
}

export function countHiddenRecommendedFields(settings: SharedFieldSettings): number {
  return MINIMUM_RECOMMENDED_KEYS.reduce((count, key) => (settings[key] ? count : count + 1), 0);
}

export function buildSharedProjectBrief(
  filters: MatchingFilters,
  settings: SharedFieldSettings,
  companyName: string,
  accountCompanyStage?: string
): SharedProjectBrief {
  const brief: SharedProjectBrief = {};

  if (settings.productTypes && filters.productType.length > 0) {
    brief.productTypes = filters.productType;
  }
  if (settings.specificServices && filters.detailedServices.length > 0) {
    brief.specificServices = filters.detailedServices;
  }
  if (settings.requiredCertifications && filters.certifications.length > 0) {
    brief.requiredCertifications = filters.certifications;
  }
  if (settings.partnerType && filters.cdmoPartnerType.trim()) {
    brief.partnerType = filters.cdmoPartnerType;
  }
  if (settings.developmentStage && filters.developmentStage.trim()) {
    brief.developmentStage = filters.developmentStage;
  }
  if (settings.supportNeeds && filters.supportType.length > 0) {
    brief.supportNeeds = filters.supportType;
  }
  if (settings.batchSizeRange && filters.batchSize.trim()) {
    brief.batchSizeRange = filters.batchSize;
  }
  if (settings.timeline && filters.speedToStart.trim()) {
    brief.timeline = filters.speedToStart;
  }
  if (settings.budgetRange && filters.budget.trim() && filters.budget !== 'Not sure / flexible') {
    brief.budgetRange = filters.budget;
  }
  if (settings.preferredRegions) {
    const preferredRegions = filters.location.filter((region) => region !== 'No preference');
    if (preferredRegions.length > 0) {
      brief.preferredRegions = preferredRegions;
    }
  }
  if (settings.companyName && companyName.trim()) {
    brief.companyName = companyName;
  }
  if (settings.accountCompanyStage && accountCompanyStage?.trim()) {
    brief.accountCompanyStage = accountCompanyStage;
  }

  return brief;
}

export function buildLegacySafeSharedBrief(requestDetails: Record<string, unknown>): SharedProjectBrief {
  const safeBrief: SharedProjectBrief = {};

  const productType = requestDetails.productType;
  if (Array.isArray(productType) && productType.length > 0) {
    safeBrief.productTypes = productType.filter((v): v is string => typeof v === 'string');
  }

  const detailedServices = requestDetails.detailedServices;
  if (Array.isArray(detailedServices) && detailedServices.length > 0) {
    safeBrief.specificServices = detailedServices.filter((v): v is string => typeof v === 'string');
  }

  const certifications = requestDetails.certifications;
  if (Array.isArray(certifications) && certifications.length > 0) {
    safeBrief.requiredCertifications = certifications
      .filter((v): v is string => typeof v === 'string')
      .map(normalizeRegulatoryCertificationLabel);
  }

  if (typeof requestDetails.cdmoPartnerType === 'string' && requestDetails.cdmoPartnerType.trim()) {
    safeBrief.partnerType = requestDetails.cdmoPartnerType;
  }
  if (typeof requestDetails.developmentStage === 'string' && requestDetails.developmentStage.trim()) {
    safeBrief.developmentStage = requestDetails.developmentStage;
  }

  const supportType = requestDetails.supportType;
  if (Array.isArray(supportType) && supportType.length > 0) {
    safeBrief.supportNeeds = supportType.filter((v): v is string => typeof v === 'string');
  }

  if (typeof requestDetails.batchSize === 'string' && requestDetails.batchSize.trim()) {
    safeBrief.batchSizeRange = requestDetails.batchSize;
  }
  if (typeof requestDetails.speedToStart === 'string' && requestDetails.speedToStart.trim()) {
    safeBrief.timeline = requestDetails.speedToStart;
  }

  const budget = requestDetails.budget;
  if (typeof budget === 'string' && budget.trim() && budget !== 'Not sure / flexible') {
    safeBrief.budgetRange = budget;
  }

  const locations = requestDetails.location;
  if (Array.isArray(locations)) {
    const preferredRegions = locations.filter(
      (value): value is string => typeof value === 'string' && value !== 'No preference'
    );
    if (preferredRegions.length > 0) {
      safeBrief.preferredRegions = preferredRegions;
    }
  }

  return safeBrief;
}

export function getSponsorDisplayName(
  sharedFieldSettings?: Partial<SharedFieldSettings> | null,
  sharedProjectBrief?: SharedProjectBrief | null
): string {
  if (sharedFieldSettings?.companyName && sharedProjectBrief?.companyName) {
    return sharedProjectBrief.companyName;
  }
  return 'Anonymous Sponsor';
}

export function isSponsorCompanyNameShared(
  sharedFieldSettings?: Partial<SharedFieldSettings> | null,
  sharedProjectBrief?: SharedProjectBrief | null
): boolean {
  return Boolean(sharedFieldSettings?.companyName && sharedProjectBrief?.companyName);
}

export interface ConversationStartupDisplayInput {
  startup_user_id: string;
  cdmo_user_id: string;
  users_startup?: { name?: string; company?: string } | null;
  introduction_request?: {
    shared_field_settings?: Partial<SharedFieldSettings> | null;
    shared_project_brief?: SharedProjectBrief | null;
  } | null;
  sponsor_display_name?: string;
}

/**
 * Resolves startup party labels for a conversation based on warm-intro sharing settings.
 * CDMO viewers only see sponsor company/contact when company name was shared on the request.
 *
 * @param conversation - Conversation row with optional intro-request sharing metadata
 * @param viewerUserId - Authenticated user viewing the conversation
 * @returns Display labels for list/header UI
 */
export function resolveStartupDisplayForViewer(
  conversation: ConversationStartupDisplayInput,
  viewerUserId: string
): { companyName: string; contactName: string } {
  if (viewerUserId === conversation.startup_user_id) {
    return {
      companyName: conversation.users_startup?.company?.trim() || 'Startup Company',
      contactName: conversation.users_startup?.name?.trim() || 'Startup Contact',
    };
  }

  const settings = conversation.introduction_request?.shared_field_settings;
  const brief = conversation.introduction_request?.shared_project_brief ?? null;
  const companyName =
    conversation.sponsor_display_name ?? getSponsorDisplayName(settings, brief);

  if (isSponsorCompanyNameShared(settings, brief)) {
    return {
      companyName,
      contactName: conversation.users_startup?.name?.trim() || 'Startup Contact',
    };
  }

  return {
    companyName,
    contactName: '',
  };
}

/**
 * Raw row from `introduction_requests` when selecting CDMO-visible columns (Supabase JSON columns are untyped).
 */
export interface CdmoIntroductionRequestDbRow {
  id?: string;
  requester_user_id: string;
  cdmo_id: string;
  shared_field_settings?: unknown | null;
  shared_project_brief?: unknown | null;
  status?: string;
  created_at?: string;
}

/**
 * Maps a DB row to the CDMO-safe introduction request shape (no sponsor email/username/raw request payload).
 *
 * @param raw - Row returned from Supabase for an introduction request
 * @returns Safe fields only, including derived `sponsor_display_name`
 */
export function toCdmoSafeIntroductionRequest(raw: CdmoIntroductionRequestDbRow): CdmoSafeIntroductionRequest {
  const sharedFieldSettings = (raw.shared_field_settings || null) as SharedFieldSettings | null;
  const sharedProjectBrief = (raw.shared_project_brief || null) as SharedProjectBrief | null;
  const safeBrief =
    sharedProjectBrief && Object.keys(sharedProjectBrief).length > 0 ? sharedProjectBrief : {};

  return {
    id: raw.id,
    requester_user_id: raw.requester_user_id,
    cdmo_id: raw.cdmo_id,
    status: raw.status,
    created_at: raw.created_at,
    shared_field_settings: sharedFieldSettings,
    shared_project_brief: safeBrief,
    sponsor_display_name: getSponsorDisplayName(sharedFieldSettings, safeBrief),
  };
}
